import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { admin, errorMessage } from "../lib/api";
import { date, TICKET_CATEGORY, TICKET_STATUS } from "../lib/format";
import type { SupportMessage, SupportTicketDetail } from "../lib/types";
import { Alert, Badge, Button, Card, PageHeader, Spinner } from "../components/ui";

export default function SupportTicket() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["support-ticket", id], queryFn: () => admin.get<SupportTicketDetail>(`/support/tickets/${id}`), refetchInterval: 30_000 });
  const [body, setBody] = useState("");
  const [error, setError] = useState<string>();

  const done = (t: SupportTicketDetail) => {
    qc.setQueryData(["support-ticket", id], t);
    qc.invalidateQueries({ queryKey: ["support"] });
    qc.invalidateQueries({ queryKey: ["support-summary"] });
    setError(undefined);
  };
  const reply = useMutation({
    mutationFn: () => admin.post<SupportTicketDetail>(`/support/tickets/${id}/messages`, { body }),
    onSuccess: (t) => { done(t); setBody(""); },
    onError: (e) => setError(errorMessage(e)),
  });
  const transition = useMutation({
    mutationFn: (action: "close" | "reopen") => admin.post<SupportTicketDetail>(`/support/tickets/${id}/${action}`),
    onSuccess: done,
    onError: (e) => setError(errorMessage(e)),
  });

  if (q.isLoading || !q.data) return <Spinner />;
  const t = q.data;
  const s = TICKET_STATUS[t.status];
  const closed = t.status === "closed";

  return (
    <>
      <PageHeader
        title={`#${t.number} · ${t.subject}`}
        subtitle={<><Link to="/support" className="hover:text-slate-900">← Suport</Link> · {TICKET_CATEGORY[t.category] ?? t.category} · deschis {date(t.created_at)}</>}
        actions={<>
          <Badge tone={s?.tone}>{s?.label ?? t.status}</Badge>
          {closed || t.status === "resolved"
            ? <Button variant="secondary" onClick={() => transition.mutate("reopen")} disabled={transition.isPending}>Redeschide</Button>
            : <Button variant="secondary" onClick={() => confirm("Închizi tichetul? Îl poți redeschide oricând.") && transition.mutate("close")} disabled={transition.isPending}>Închide tichetul</Button>}
        </>}
      />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      <div className="max-w-3xl space-y-4">
        <ol className="space-y-4">{t.messages.map((m) => <Message key={m.id} m={m} />)}</ol>
        {closed ? (
          <Alert tone="amber">Tichetul e închis. Redeschide-l dacă problema persistă.</Alert>
        ) : (
          <Card>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); reply.mutate(); }}>
              <textarea className="input min-h-28" placeholder="Scrie un răspuns…" maxLength={10000} value={body} onChange={(e) => setBody(e.target.value)} />
              <div className="flex justify-end"><Button disabled={!body.trim() || reply.isPending}>Trimite</Button></div>
            </form>
          </Card>
        )}
      </div>
    </>
  );
}

function Message({ m }: { m: SupportMessage }) {
  if (m.author_kind === "system") {
    return <li className="text-center text-xs text-slate-400">{m.body} · {date(m.created_at)}</li>;
  }
  const team = m.author_kind === "operator";
  return (
    <li className={`rounded-xl border p-4 ${team ? "border-brand/20 bg-brand-soft" : "border-slate-200 bg-white"}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-slate-800">{m.author_name}</span>
        {team && <Badge tone="violet">echipa shopcore</Badge>}
        <span className="ml-auto text-slate-400">{date(m.created_at)}</span>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-slate-800">{m.body}</p>
    </li>
  );
}
